// @ts-check
import { computed, ref, watch } from 'vue';

import { ListEnvelope, Meta } from '@/store/modules/envelopes/models';

import useList from '@/composables/list/list';

// eslint-disable-next-line import/prefer-default-export
export function useEnvelopesList() {
  const [fetch, baseResults, baseMeta, isLoading, refresh] = useList(
    'envelopes',
    ListEnvelope,
    Meta,
  );

  // create map of fingerprint to statuses
  const baseStatuses = computed(() =>
    Object.fromEntries(baseResults.value.map(item => [item.fingerprint, item.status])));

  const deleted = ref([]);
  const cancelled = ref([]);
  const activeStatuses = ref([]);

  /**
   * Mark envelope deleted to remove it from the list
   *
   * @param {string} fingerprint
   */
  function markEnvelopeDeleted(fingerprint) {
    deleted.value.push(fingerprint);
  }

  /**
   * Mark envelope cancelled to change it's status in the list
   *
   * @param {string} fingerprint
   */
  function markEnvelopeCancelled(fingerprint, statuses) {
    activeStatuses.value = statuses;
    cancelled.value.push(fingerprint);
  }

  // reset deleted and cancelled when the list has changed
  watch(baseResults, (_, old) => {
    if (old.length) {
      deleted.value = [];
      cancelled.value = [];
      activeStatuses.value = [];
    }
  });

  // filter out deleted and update cancelled
  const results = computed(() =>
    baseResults.value
      .filter((item) => {
        if (deleted.value.includes(item.fingerprint)) {
          return false;
        }

        if (
            activeStatuses.value.length &&
            !activeStatuses.value.includes('cancelled') &&
            cancelled.value.includes(item.fingerprint)
        ) {
          return false;
        }

        return true;
      })
      .map(item =>
        (cancelled.value.includes(item.fingerprint)
          ? { ...item, status: 'cancelled' }
          : item)));

  // update aggregates and counts
  const meta = computed(() => {
    if (!baseMeta.value?.to || !baseMeta.value?.total || !baseMeta.value?.aggregates) {
      return baseMeta.value;
    }

    // copy aggregates and counts
    const updatedAggregates = { ...baseMeta.value.aggregates };
    let { to: updatedTo, total: updatedTotal } = baseMeta.value;

    // decrement aggregate status of deleted and counts
    deleted.value.forEach((fingerprint) => {
      updatedAggregates[baseStatuses.value[fingerprint]] -= 1;
      updatedTo -= 1;
      updatedTotal -= 1;
    });

    // filter out deleted then adjust aggregate status of cancelled
    cancelled.value
      .filter(fingerprint => !deleted.value.includes(fingerprint))
      .forEach((fingerprint) => {
        updatedAggregates[baseStatuses.value[fingerprint]] -= 1;
        updatedAggregates.cancelled += 1;
        if (
            activeStatuses.value.length &&
            !activeStatuses.value.includes('cancelled') &&
            cancelled.value.includes(fingerprint)
        ) {
          updatedTo -= 1;
          updatedTotal -= 1;
        }
      });

    return {
      ...baseMeta.value,
      to: updatedTo,
      total: updatedTotal,
      aggregates: updatedAggregates,
    };
  });

  return {
    fetch,
    results,
    meta,
    isLoading,
    refresh,
    markEnvelopeDeleted,
    markEnvelopeCancelled,
  };
}
